import React, { useState, useEffect } from 'react';
import { useSpring, animated } from 'react-spring';

interface TypewriterValueProps {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  delay?: number;
  className?: string;
  formatter?: (value: number) => string;
}

const TypewriterValue: React.FC<TypewriterValueProps> = ({
  value,
  prefix = '',
  suffix = '',
  duration = 1500,
  delay = 0,
  className = '',
  formatter = (val) => val.toFixed(2)
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Animação do valor usando react-spring
  const { val } = useSpring({
    from: { val: 0 },
    to: { val: value },
    delay,
    config: { duration },
    onRest: () => {
      // Garantir que o valor final seja exato
      setDisplayValue(formatter(value));
      setIsTyping(false);
    }
  });
  
  // Efeito de digitação
  useEffect(() => {
    setIsTyping(true);
    
    // Formatar o valor final para determinar o comprimento
    const formattedValue = formatter(value);
    let currentIndex = 0;
    
    // Simular efeito de digitação
    const typingInterval = setInterval(() => {
      if (currentIndex <= formattedValue.length) {
        setDisplayValue(formattedValue.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setIsTyping(false);
      }
    }, duration / formattedValue.length);
    
    return () => clearInterval(typingInterval);
  }, [value, duration, formatter]);
  
  // Efeito de cursor piscando
  const cursorAnimation = useSpring({
    opacity: isTyping ? 1 : 0,
    from: { opacity: 0 },
    loop: isTyping ? { reverse: true } : false,
    config: { duration: 400 }
  });
  
  return (
    <span className={`typewriter-value ${className}`}>
      {prefix}
      <animated.span>
        {val.to(v => formatter(v))}
      </animated.span>
      {suffix}
      <animated.span 
        style={cursorAnimation}
        className="cursor"
      >
        |
      </animated.span>
    </span>
  );
};

export default TypewriterValue; 